import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {DatePipe} from '@angular/common';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Observable, Subscription} from 'rxjs';
import {TuiDialogService} from '@taiga-ui/core';
import {GateweeTableColumn} from '@gatewee/interfaces';
import {DomainUser, RecipientUser, UserStatus} from '@gatewee/types/user';
import {AdminUser} from '@gatewee/types/admin-user';

export type UserEntity = AdminUser | RecipientUser | DomainUser;

export interface UserActions {
  onEdit?: (user: UserEntity) => void;
  onOpen?: (user: UserEntity) => void;
  onDelete?: (user: UserEntity) => Observable<void>;
  onInvite?: (email: string, role: string) => Observable<void>;
  onResendInvitation?: (user: UserEntity) => Observable<void>;
  onSuspendAccess?: (user: UserEntity) => Observable<void>;
  onActivateAccess?: (user: UserEntity) => Observable<void>;
}

@Component({
  selector: 'gatewee-users',
  templateUrl: './gatewee-users.component.html',
  styleUrls: ['./gatewee-users.component.css'],
  standalone: false,
  providers: [DatePipe],
})
export class GateweeUsersComponent implements OnInit {
  @Input() users: UserEntity[] = [];
  @Input() actions!: UserActions;
  @Input() disableContentPage = false;
  @Input() breadcrumb: {label: string; path?: string}[] = [];
  @Input() title = 'features.users.title';
  @Input() titlePath = 'features.users.titlePath';
  @Input() page = 1;
  @Input() pageSize = 10;
  @Input() total = 0;
  @Output() pageChange = new EventEmitter<number>();

  @ViewChild('inviteUserModal') inviteUserModal!: TemplateRef<never>;
  @ViewChild('userViewModal') userViewModal!: TemplateRef<never>;

  columns: GateweeTableColumn[] = [];
  inviteForm!: FormGroup;
  selectedUser: UserEntity | null = null;
  loading = false;
  private inviteModalSub?: Subscription;

  roles = [
    {value: 'admin', label: 'features.users.inviteModal.roles.admin'},
    {value: 'user', label: 'features.users.inviteModal.roles.user'},
  ];

  constructor(
    private dialogService: TuiDialogService,
    private fb: FormBuilder,
    private datePipe: DatePipe,
  ) {
    this.inviteForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      role: ['admin'],
    });
  }

  ngOnInit(): void {
    this.setupColumns();
  }

  private setupColumns(): void {
    this.columns = [
      {
        label: 'features.users.table.columns.id',
        field: 'id',
        sortable: false,
        visible: true,
        alwaysVisible: false,
      },
      {
        label: 'features.users.table.columns.name',
        field: 'name',
        sortable: false,
        visible: true,
        alwaysVisible: true,
      },
      {
        label: 'features.users.table.columns.email',
        field: 'email',
        sortable: false,
        visible: true,
        alwaysVisible: true,
      },
      {
        label: 'features.users.table.columns.status',
        field: 'status',
        sortable: false,
        visible: true,
        badge: true,
        pipe: {
          fn: {
            transform: (value: UserStatus | unknown) =>
              this.getStatusLabel(value),
          },
          args: [],
        },
      },
      {
        label: 'features.users.table.columns.role',
        field: 'role',
        sortable: false,
        visible: true,
        pipe: {
          fn: {
            transform: (value: string) => (value ? value.toUpperCase() : value),
          },
          args: [],
        },
      },
      {
        label: 'features.users.table.columns.createdAt',
        field: 'createdAt',
        sortable: false,
        visible: true,
        pipe: {
          fn: this.datePipe,
          args: ['dd/MM/yyyy HH:mm:ss'],
        },
      },
      {
        label: 'features.users.table.columns.lastLogin',
        field: 'lastLoginContext.date',
        sortable: false,
        visible: true,
        pipe: {
          fn: this.datePipe,
          args: ['dd/MM/yyyy HH:mm:ss'],
        },
      },
      {
        label: 'features.users.table.columns.actions',
        field: 'actions',
        sortable: false,
        visible: true,
        alwaysVisible: true,
        actions: [
          {
            key: 'view',
            label: 'features.users.actions.open',
          },
          {
            key: 'resend_invite',
            icon: '@phi.paper-plane-tilt',
            label: 'features.users.table.menu.resendInvite',
            condition: {status: 'pending'},
          },
          {
            key: 'edit',
            label: 'features.users.actions.edit',
          },
          {
            key: 'delete',
            label: 'features.users.actions.delete',
          },
        ],
      },
    ];
  }

  onActionClicked(event: {row: UserEntity; type: string}): void {
    const {row, type} = event;

    switch (type) {
      case 'view':
        this.openUserView(row);
        break;
      case 'resend_invite':
        this.handleResendInvitation(row);
        break;
      case 'edit':
        if (this.actions.onEdit) {
          this.actions.onEdit(row);
        }
        break;
      case 'delete':
        if (this.actions.onDelete) {
          this.handleDelete(row);
        }
        break;
    }
  }

  openUserView(user: UserEntity): void {
    this.selectedUser = user;
    this.dialogService
      .open(this.userViewModal, {
        size: 'l',
        closeable: true,
      })
      .subscribe({
        complete: () => {
          this.selectedUser = null;
        },
      });
  }

  openInviteModal(): void {
    this.inviteForm.reset();
    this.inviteModalSub?.unsubscribe();
    this.inviteModalSub = this.dialogService
      .open(this.inviteUserModal, {
        size: 'm',
        closeable: true,
      })
      .subscribe({
        complete: () => {
          this.inviteModalSub = undefined;
          this.inviteForm.reset();
        },
      });
  }

  handleInvite(close: unknown): void {
    if (this.inviteForm.invalid || !this.actions.onInvite) {
      return;
    }

    const {email, role} = this.inviteForm.value;
    this.loading = true;

    this.actions.onInvite(email, role).subscribe({
      next: () => {
        this.loading = false;
        this.closeInviteModal(close);
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  closeInviteModal(close: unknown): void {
    if (typeof close === 'function') {
      close();
    } else if (
      close &&
      typeof close === 'object' &&
      'complete' in close &&
      typeof (close as {complete?: () => void}).complete === 'function'
    ) {
      (close as {complete: () => void}).complete();
    }
    this.inviteModalSub?.unsubscribe();
    this.inviteModalSub = undefined;
  }

  handleDelete(user: UserEntity): void {
    if (!this.actions.onDelete) return;

    // You can add a confirmation dialog here if needed
    this.actions.onDelete(user).subscribe({
      next: () => {
        // Success handled by parent
      },
      error: () => {
        // Error handled by parent
      },
    });
  }

  handleResendInvitation(user: UserEntity): void {
    if (!this.actions.onResendInvitation) return;

    this.loading = true;
    this.actions.onResendInvitation(user).subscribe({
      next: () => {
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  handleSuspendAccess(user: UserEntity): void {
    if (!this.actions.onSuspendAccess) return;

    this.loading = true;
    this.actions.onSuspendAccess(user).subscribe({
      next: () => {
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  handleActivateAccess(user: UserEntity): void {
    if (!this.actions.onActivateAccess) return;

    this.loading = true;
    this.actions.onActivateAccess(user).subscribe({
      next: () => {
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  getStatusClass(status: UserStatus | unknown): string {
    // tuiStatus expects specific color values
    switch (status) {
      case UserStatus.ACTIVE:
        return '#39B54A'; // success/green
      case UserStatus.PENDING:
        return '#FFA726'; // warning/orange
      case UserStatus.INACTIVE:
      case UserStatus.BLOCKED:
        return '#FF7043'; // error/red
      default:
        return '#526ED3'; // info/blue
    }
  }

  getStatusLabel(status: UserStatus | unknown): string {
    switch (status) {
      case UserStatus.ACTIVE:
        return 'enums.userStatus.active';
      case UserStatus.PENDING:
        return 'enums.userStatus.pending';
      case UserStatus.INACTIVE:
        return 'enums.userStatus.inactive';
      case UserStatus.BLOCKED:
        return 'enums.userStatus.blocked';
      default:
        return 'enums.userStatus.unknown';
    }
  }

  isPending(user: UserEntity): boolean {
    return user.status === UserStatus.PENDING;
  }

  isActive(user: UserEntity): boolean {
    return user.status === UserStatus.ACTIVE;
  }

  isSuspended(user: UserEntity): boolean {
    return (
      user.status === UserStatus.INACTIVE || user.status === UserStatus.BLOCKED
    );
  }

  onPageChange(page: number): void {
    this.pageChange.emit(page);
  }
}
